// TV ↔ server WebSocket protocol and shared data types.
// The server, the TV runtime and the panel all use these types.

// ---------- i18n ----------

/** Languages the TV, the apps (modules) and the panel can be shown in. */
export const LOCALES = ['en', 'tr'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';

/** Each language's name in its own language (for pickers). */
export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', tr: 'Türkçe' };

/** BCP 47 tags for Intl (dates, numbers, collation). */
export const LOCALE_TAGS: Record<Locale, string> = { en: 'en-US', tr: 'tr-TR' };

export function isLocale(v: unknown): v is Locale {
  return typeof v === 'string' && (LOCALES as readonly string[]).includes(v);
}

/**
 * User-facing text in a manifest: either a plain string (same in every language) or one
 * string per language. English is required and is the fallback for missing languages.
 * Example: name: { en: 'Weather', tr: 'Hava durumu' }
 */
export type LocalizedText = string | ({ en: string } & Partial<Record<Locale, string>>);

/** Resolve a LocalizedText for a language (falls back to English). */
export function localize(text: LocalizedText, locale: Locale): string;
export function localize(text: LocalizedText | undefined, locale: Locale): string | undefined;
export function localize(text: LocalizedText | undefined, locale: Locale): string | undefined {
  if (text == null || typeof text === 'string') return text;
  return text[locale] ?? text.en;
}

// ---------- screen ----------

/** settings: sections of the TV's Settings app (opened with the core/settings tile). */
export type ZoneName = 'background' | 'launcher' | 'gallery' | 'settings';

export interface WidgetInstance {
  /** Stable id (w_…); does not change across revisions. */
  id: string;
  /** "<module>/<widget type>", e.g. "hue/scene-button". */
  type: string;
  settings: Record<string, unknown>;
  /** Position in the launcher grid (launcher zone only). */
  layout?: WidgetLayout;
}

/**
 * Launcher grid: `ScreenDoc.launcherGrid.cols` columns, square cells. Occupies w×h cells (any size that fits the
 * grid); if x/y (0-based) are given the widget is pinned to that cell, otherwise it flows into free cells in list order.
 */
export interface WidgetLayout {
  w: number;
  h: number;
  x?: number;
  y?: number;
}

/** Launcher grid settings (screen-wide). Cells are (nearly) square: their size follows from the columns, gap and paddings. */
export interface LauncherGrid {
  /** Number of columns (GRID.minCols … GRID.maxCols). */
  cols: number;
  /** Space between cells, px. */
  gap: number;
  /** Space between the grid and the screen edges, px (0 = flush with the edge). */
  padding: { top: number; right: number; bottom: number; left: number };
}

/** The TV's fixed CSS viewport. */
export const SCREEN = { width: 1920, height: 1080 } as const;

/** Limits of the grid settings and of widget sizes (a widget can never be bigger than the actual grid, see clampSize). */
export const GRID = { minCols: 4, maxCols: 24, maxRows: 24, maxGap: 48, maxPadding: 240 } as const;

/**
 * Widgets are laid out in a fixed "design" coordinate space: the cell edge of the default grid. The runtime scales every launcher
 * widget by `GridMetrics.scale` (= cell / GRID_REF_CELL), so widget CSS written in px keeps its proportions when the grid changes.
 */
export const GRID_REF_CELL = 122;

/** Default = the original fixed layout: 12 columns, 122 px cells, 24 px gap, 96 px side margins. */
export const DEFAULT_LAUNCHER_GRID: LauncherGrid = { cols: 12, gap: 24, padding: { top: 80, right: 96, bottom: 0, left: 96 } };

const clampInt = (v: unknown, min: number, max: number, fallback: number) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : fallback;
};

/** Fill in defaults and clamp every field to its limits (never throws). */
export function normalizeLauncherGrid(g?: Partial<LauncherGrid> & { padding?: Partial<LauncherGrid['padding']> }): LauncherGrid {
  const d = DEFAULT_LAUNCHER_GRID;
  const p: Partial<LauncherGrid['padding']> = g?.padding ?? {};
  return {
    cols: clampInt(g?.cols, GRID.minCols, GRID.maxCols, d.cols),
    gap: clampInt(g?.gap, 0, GRID.maxGap, d.gap),
    padding: {
      top: clampInt(p.top, 0, GRID.maxPadding, d.padding.top),
      right: clampInt(p.right, 0, GRID.maxPadding, d.padding.right),
      bottom: clampInt(p.bottom, 0, GRID.maxPadding, d.padding.bottom),
      left: clampInt(p.left, 0, GRID.maxPadding, d.padding.left),
    },
  };
}

/** Derived geometry of a launcher grid on the 1920×1080 screen. */
export interface GridMetrics {
  cols: number;
  gap: number;
  /** Cell width, px (may be fractional). */
  cell: number;
  /** Cell height, px: `cell` stretched by a few percent at most so the visible rows fill the padded height exactly (bottom edge flush). */
  cellH: number;
  /** Rows that fit fully inside the padded screen without scrolling (≥ 1). */
  visibleRows: number;
  /** cell / GRID_REF_CELL: how much the runtime scales widget content relative to the design space (1 on the default grid). */
  scale: number;
}

export function gridMetrics(grid?: LauncherGrid): GridMetrics {
  const g = normalizeLauncherGrid(grid);
  const cell = (SCREEN.width - g.padding.left - g.padding.right - (g.cols - 1) * g.gap) / g.cols;
  const visibleRows = Math.max(1, Math.floor((SCREEN.height - g.padding.top - g.padding.bottom + g.gap) / (cell + g.gap) + 1e-6));
  const cellH = (SCREEN.height - g.padding.top - g.padding.bottom + g.gap) / visibleRows - g.gap;
  return { cols: g.cols, gap: g.gap, cell, cellH, visibleRows, scale: cell / GRID_REF_CELL };
}

/** px width of n columns (including the gaps between them). */
export const cellsToPx = (m: Pick<GridMetrics, 'cell' | 'gap'>, n: number) => n * m.cell + (n - 1) * m.gap;
/** px height of n rows (including the gaps between them). */
export const rowsToPx = (m: Pick<GridMetrics, 'cellH' | 'gap'>, n: number) => n * m.cellH + (n - 1) * m.gap;

/** "4x2" → { w: 4, h: 2 }. Bounds are the absolute grid limits; use clampSize to fit a particular grid. */
export function parseSize(size: string): { w: number; h: number } | undefined {
  const m = /^(\d+)x(\d+)$/.exec(size);
  if (!m) return undefined;
  const w = Number(m[1]);
  const h = Number(m[2]);
  return w >= 1 && w <= GRID.maxCols && h >= 1 && h <= GRID.maxRows ? { w, h } : undefined;
}

/** Shrink a size so it fits a grid with `cols` columns. */
export function clampSize(s: { w: number; h: number }, cols: number): { w: number; h: number } {
  return { w: Math.max(1, Math.min(s.w, cols)), h: Math.max(1, Math.min(s.h, GRID.maxRows)) };
}

/**
 * Suggested sizes (presets) for a widget that does not declare its own. Any size that fits the grid is allowed:
 * `sizes` in a widget manifest is only a list of suggestions.
 */
export const DEFAULT_SIZES = ['1x1', '2x1', '3x1', '4x1', '2x2', '4x2', '6x2', '4x3', '6x3', '12x2', '12x3'];

export interface ScreenDoc {
  theme: Record<string, string>;
  /** Inactivity before switching to gallery (idle) mode. */
  ambient: { idleSeconds: number };
  /** Language of the TV and the apps on it. */
  locale: Locale;
  zones: Record<ZoneName, WidgetInstance[]>;
  /** Launcher grid settings; absent = DEFAULT_LAUNCHER_GRID. Always read through normalizeLauncherGrid. */
  launcherGrid?: LauncherGrid;
  /** Document already migrated to the grid layout (old revisions are converted once). */
  grid?: 1;
}

/** Module bundles the TV loads (content-hashed URLs). */
export type BundleMap = Record<string, { js: string; css?: string }>;

// ---------- TV → server ----------

export type TvMessage =
  | {
      t: 'hello';
      deviceId: string;
      name: string;
      model?: string;
      token?: string;
      runtime: string;
      /** 'tv' is a real device, 'preview' the panel preview. */
      role: 'tv' | 'preview';
      draft?: boolean;
    }
  | { t: 'action'; reqId: string; widgetId: string; action: string; input?: unknown }
  | { t: 'lan:response'; reqId: string; ok: boolean; status?: number; body?: string; headers?: Record<string, string>; error?: string }
  | { t: 'tv:response'; reqId: string; ok: boolean; result?: unknown; error?: string }
  | { t: 'log'; level: 'info' | 'warn' | 'error'; source: string; message: string }
  | { t: 'ping' };

// ---------- server → TV ----------

export type ServerMessage =
  | { t: 'welcome'; deviceId: string; approved: boolean; token?: string; /** Current runtime.js content hash; the TV reloads if it differs. */ runtime?: string; locale?: Locale }
  | { t: 'screen'; revision: number; doc: ScreenDoc; bundles: BundleMap }
  | { t: 'action:result'; reqId: string; ok: boolean; data?: unknown; error?: string }
  | { t: 'channel'; module: string; channel: string; data: unknown }
  | { t: 'lan:request'; reqId: string; url: string; method: string; headers?: Record<string, string>; body?: string; timeoutMs: number }
  | { t: 'tv:request'; reqId: string; api: string; args: unknown[] }
  | { t: 'command'; action: TvCommand; data?: unknown }
  | { t: 'reload' }
  | { t: 'pong' };

export type TvCommand = 'sleep' | 'wake' | 'next' | 'reload' | 'toast';

// ---------- module manifest (extracted by the server from the module code) ----------

export interface ModuleManifest {
  id: string;
  name: LocalizedText;
  description?: LocalizedText;
  permissions: Permission[];
  /** JSON schema of module-level settings (panel form). */
  settings?: JsonSchema;
  actions: Record<
    string,
    {
      description?: LocalizedText;
      input?: JsonSchema;
      widgetOnly?: boolean;
      panel?: PanelAction;
      /** Exposed as a tool (description for models, English). */
      expose?: string;
    }
  >;
  widgets: Record<string, WidgetManifest>;
  schedules: Record<string, ScheduleInfo>;
  routes: string[];
  /** Cross-module events it listens to (ctx.emit). */
  events: string[];
}

/** Show a button for this action in the panel. */
export interface PanelAction {
  label: LocalizedText;
  confirm?: LocalizedText;
  /** Show the result in the panel. */
  showResult?: boolean;
  /** If the result is a URL (or { url }), open it in a new tab (e.g. linking an account via OAuth). */
  openUrl?: boolean;
}

export interface ScheduleInfo {
  every: number;
  whenEdgeOnline?: boolean;
  runOnStart?: boolean;
}

export interface WidgetManifest {
  name: LocalizedText;
  description?: LocalizedText;
  zones: ZoneName[];
  settings?: JsonSchema;
  /** Sizes selectable in the launcher ("2x2", "4x2"…); DEFAULT_SIZES if absent. */
  sizes?: string[];
  /** Size used when added (sizes[0] if absent). */
  defaultSize?: string;
}

/** tools: call actions other modules expose, as tools (ctx.tools). */
export type Permission = 'lan' | 'tv' | 'http' | 'tools';

export interface ToolInfo {
  /** 'module.action' or the built-in 'tv.command' */
  name: string;
  description: string;
  input?: JsonSchema;
}

export type JsonSchema = Record<string, unknown>;

/**
 * Extra hints for form fields (given with zod .meta, carried into the JSON schema).
 * Use `label`/`help` for text shown in the panel; they may be localized. (zod types `title` and
 * `description` as plain strings; they still work as unlocalized fallbacks.)
 */
export interface FieldMeta {
  /** Field label, e.g. { en: 'City', tr: 'Şehir' }. */
  label?: LocalizedText;
  /** Help text under the field. */
  help?: LocalizedText;
  title?: string;
  description?: string;
  /** Load options live from this module action: [{ value, label }]. */
  options?: string;
  widget?: 'slider' | 'color' | 'textarea' | 'location' | 'app' | 'hidden';
  /** Labels for enum values: { hero: { en: 'Large', tr: 'Büyük' } }. */
  enumLabels?: Record<string, LocalizedText>;
}

export interface Option {
  value: string;
  label: string;
  hint?: string;
}
